import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MyCart, CartProduct, MyWishlist, WishlistProduct } from '../models/cart.model';
import { DEFAULT_CID } from '../config/api.config';

const CART_STORAGE_KEY = 'ravija_jewels_cart_v1';
const WISHLIST_STORAGE_KEY = 'ravija_jewels_wishlist_v1';

const EMPTY_CART: MyCart = {
  myCartId: 0,
  userId: 0,
  cid: DEFAULT_CID,
  productList: [],
  totalPrice: 0,
  totalTaxAmount: 0
};

const EMPTY_WISHLIST: MyWishlist = {
  wishListId: 0,
  userId: 0,
  cid: DEFAULT_CID,
  productList: []
};

@Injectable({
  providedIn: 'root'
})
export class CartStoreService {
  private cartSubject: BehaviorSubject<MyCart>;
  public cart$: Observable<MyCart>;

  private wishlistSubject: BehaviorSubject<MyWishlist>;
  public wishlist$: Observable<MyWishlist>;

  readonly cartCount = signal<number>(0);
  readonly wishlistCount = signal<number>(0);

  constructor() {
    // Purge legacy unauthenticated storage if present
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(CART_STORAGE_KEY);
        localStorage.removeItem(WISHLIST_STORAGE_KEY);
      } catch {}
    }

    const initialCart = this.loadCartFromStorage();
    const initialWishlist = this.loadWishlistFromStorage();

    this.cartSubject = new BehaviorSubject<MyCart>(initialCart);
    this.cart$ = this.cartSubject.asObservable();

    this.wishlistSubject = new BehaviorSubject<MyWishlist>(initialWishlist);
    this.wishlist$ = this.wishlistSubject.asObservable();

    this.updateSignals(initialCart, initialWishlist);
  }

  private getLoggedInUserId(): number {
    if (typeof window === 'undefined') return 0;
    try {
      const data = sessionStorage.getItem('userCreds');
      if (data) {
        const parsed = JSON.parse(data);
        return Number(parsed?.userId || 0);
      }
    } catch {
      return 0;
    }
    return 0;
  }

  loadUserStore(userId: number): void {
    const userCart = this.loadCartFromStorage(userId);
    const userWishlist = this.loadWishlistFromStorage(userId);
    this.cartSubject.next(userCart);
    this.wishlistSubject.next(userWishlist);
    this.updateSignals(userCart, userWishlist);
  }

  clearSession(): void {
    this.cartSubject.next({ ...EMPTY_CART });
    this.wishlistSubject.next({ ...EMPTY_WISHLIST });
    this.cartCount.set(0);
    this.wishlistCount.set(0);
  }

  private updateSignals(cart: MyCart, wishlist: MyWishlist): void {
    const uid = this.getLoggedInUserId();
    if (!uid) {
      this.cartCount.set(0);
      this.wishlistCount.set(0);
      return;
    }
    const cCount = cart.productList ? cart.productList.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
    const wCount = wishlist.productList ? wishlist.productList.length : 0;
    this.cartCount.set(cCount);
    this.wishlistCount.set(wCount);
  }

  private loadCartFromStorage(targetUserId?: number): MyCart {
    if (typeof window === 'undefined') return { ...EMPTY_CART };
    const uid = targetUserId || this.getLoggedInUserId();
    if (!uid) {
      return { ...EMPTY_CART };
    }

    try {
      const data = localStorage.getItem(`ravija_user_${uid}_cart`);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.warn('Could not read user cart from localStorage', e);
    }
    return { ...EMPTY_CART, userId: uid };
  }

  private saveCartToStorage(cart: MyCart): void {
    if (typeof window === 'undefined') return;
    const uid = cart.userId || this.getLoggedInUserId();
    if (!uid) return;
    try {
      localStorage.setItem(`ravija_user_${uid}_cart`, JSON.stringify(cart));
    } catch (e) {
      console.warn('Could not save user cart to localStorage', e);
    }
  }

  private loadWishlistFromStorage(targetUserId?: number): MyWishlist {
    if (typeof window === 'undefined') return { ...EMPTY_WISHLIST };
    const uid = targetUserId || this.getLoggedInUserId();
    if (!uid) {
      return { ...EMPTY_WISHLIST };
    }

    try {
      const data = localStorage.getItem(`ravija_user_${uid}_wishlist`);
      if (data) {
        return JSON.parse(data);
      }
    } catch (e) {
      console.warn('Could not read user wishlist from localStorage', e);
    }
    return { ...EMPTY_WISHLIST, userId: uid };
  }

  private saveWishlistToStorage(wishlist: MyWishlist): void {
    if (typeof window === 'undefined') return;
    const uid = wishlist.userId || this.getLoggedInUserId();
    if (!uid) return;
    try {
      localStorage.setItem(`ravija_user_${uid}_wishlist`, JSON.stringify(wishlist));
    } catch (e) {
      console.warn('Could not save user wishlist to localStorage', e);
    }
  }


  private recalculateCart(items: CartProduct[], cartId: number = 0, userId: number = 0, cid: number = DEFAULT_CID): MyCart {
    let subTotalSum = 0;
    let totalTax = 0;

    const normalizedList: CartProduct[] = items.map(p => {
      const qty = Math.max(1, p.quantity || 1);
      const sub = (p.price || 0) * qty;
      const tax = Math.round(sub * 0.03); // 3% fine jewelry GST
      subTotalSum += sub;
      totalTax += tax;

      return {
        ...p,
        quantity: qty,
        subTotal: sub,
        taxAmount: tax
      };
    });

    return {
      myCartId: cartId,
      userId: userId,
      cid: cid,
      productList: normalizedList,
      totalPrice: subTotalSum + totalTax,
      totalTaxAmount: totalTax
    };
  }

  getCart(): MyCart {
    return this.cartSubject.getValue();
  }

  getWishlist(): MyWishlist {
    return this.wishlistSubject.getValue();
  }

  addToCart(item: any): MyCart {
    const currentCart = this.getCart();
    let productList = [...(currentCart.productList || [])];

    const prodId = Number(item.productId);
    const existingIndex = productList.findIndex(p => p.productId === prodId);

    const price = Number(item.price || item.activePrice || 0);
    const name = item.productName || item.name || 'Fine Luxury Jewelry Item';
    const image = item.productImageUrl || item.imageUrl || 'assets/images/jewelry/ring-1.jpg';
    const uom = item.uom || 'Piece';
    const color = item.color || 'Gold';
    const sku = item.sku || 'RAVIJA-JW-001';
    const addQty = Math.max(1, Number(item.quantity) || 1);

    if (existingIndex > -1) {
      const existing = productList[existingIndex];
      const newQty = existing.quantity + addQty;
      productList[existingIndex] = {
        ...existing,
        quantity: newQty
      };
    } else {
      productList.push({
        productId: prodId,
        productName: name,
        productImageUrl: image,
        sku: sku,
        uom: uom,
        quantity: addQty,
        price: price,
        color: color
      });
    }

    const updatedCart = this.recalculateCart(productList, currentCart.myCartId, currentCart.userId, currentCart.cid);
    this.saveCartToStorage(updatedCart);
    this.cartSubject.next(updatedCart);
    this.updateSignals(updatedCart, this.getWishlist());
    return updatedCart;
  }

  updateQuantity(productId: number, quantity: number): MyCart {
    const currentCart = this.getCart();
    let productList = [...(currentCart.productList || [])];

    const prodId = Number(productId);
    if (quantity <= 0) {
      productList = productList.filter(p => p.productId !== prodId);
    } else {
      const index = productList.findIndex(p => p.productId === prodId);
      if (index > -1) {
        productList[index] = {
          ...productList[index],
          quantity: quantity
        };
      }
    }

    const updatedCart = this.recalculateCart(productList, currentCart.myCartId, currentCart.userId, currentCart.cid);
    this.saveCartToStorage(updatedCart);
    this.cartSubject.next(updatedCart);
    this.updateSignals(updatedCart, this.getWishlist());
    return updatedCart;
  }

  removeFromCart(productId: number): MyCart {
    const currentCart = this.getCart();
    const prodId = Number(productId);
    const productList = (currentCart.productList || []).filter(p => p.productId !== prodId);

    const updatedCart = this.recalculateCart(productList, currentCart.myCartId, currentCart.userId, currentCart.cid);
    this.saveCartToStorage(updatedCart);
    this.cartSubject.next(updatedCart);
    this.updateSignals(updatedCart, this.getWishlist());
    return updatedCart;
  }

  clearCart(): void {
    const currentCart = this.getCart();
    const updatedCart = this.recalculateCart([], currentCart.myCartId, currentCart.userId, currentCart.cid);
    this.saveCartToStorage(updatedCart);
    this.cartSubject.next(updatedCart);
    this.updateSignals(updatedCart, this.getWishlist());
  }

  addToWishlist(item: any): MyWishlist {
    const currentWishlist = this.getWishlist();
    const productList = [...(currentWishlist.productList || [])];

    const prodId = Number(item.productId);
    const existing = productList.find(p => p.productId === prodId);

    if (!existing) {
      const price = Number(item.price || 0);
      const name = item.productName || item.name || 'Fine Luxury Jewelry Item';
      const image = item.productImageUrl || item.imageUrl || 'assets/images/jewelry/ring-1.jpg';
      const uom = item.uom || 'Piece';
      const color = item.color || 'Gold';
      const sku = item.sku || 'RAVIJA-JW-001';

      productList.push({
        productId: prodId,
        productName: name,
        productImageUrl: image,
        sku: sku,
        uom: uom,
        quantity: 1,
        price: price,
        color: color,
        subTotal: price,
        taxAmount: Math.round(price * 0.03)
      });

      const updatedWishlist: MyWishlist = {
        ...currentWishlist,
        productList: productList
      };

      this.saveWishlistToStorage(updatedWishlist);
      this.wishlistSubject.next(updatedWishlist);
      this.updateSignals(this.getCart(), updatedWishlist);
      return updatedWishlist;
    }

    return currentWishlist;
  }

  removeFromWishlist(productId: number): MyWishlist {
    const currentWishlist = this.getWishlist();
    const prodId = Number(productId);
    const productList = (currentWishlist.productList || []).filter(p => p.productId !== prodId);

    const updatedWishlist: MyWishlist = {
      ...currentWishlist,
      productList: productList
    };

    this.saveWishlistToStorage(updatedWishlist);
    this.wishlistSubject.next(updatedWishlist);
    this.updateSignals(this.getCart(), updatedWishlist);
    return updatedWishlist;
  }

  moveToWishlist(productId: number): void {
    const currentCart = this.getCart();
    const prodId = Number(productId);
    const item = (currentCart.productList || []).find(p => p.productId === prodId);

    if (item) {
      this.addToWishlist(item);
      this.removeFromCart(prodId);
    }
  }

  moveToCart(productId: number): void {
    const currentWishlist = this.getWishlist();
    const prodId = Number(productId);
    const item = (currentWishlist.productList || []).find(p => p.productId === prodId);

    if (item) {
      this.addToCart({
        ...item,
        quantity: 1
      });
      this.removeFromWishlist(prodId);
    }
  }
}
